import { useCallback, useRef } from 'react';
import { useEffect, useState } from 'react';
export var UseTrackImpressionTrackStatus;
(function (UseTrackImpressionTrackStatus) {
    UseTrackImpressionTrackStatus["loading"] = "loading";
    UseTrackImpressionTrackStatus["initial"] = "initial";
    UseTrackImpressionTrackStatus["rendered"] = "rendered";
    UseTrackImpressionTrackStatus["impressionFired"] = "impressionFired";
})(UseTrackImpressionTrackStatus || (UseTrackImpressionTrackStatus = {}));
/**
 * @description Used to track impressions even when element is inside condition,
 * by waiting for the element to render before attaching the intersection observer,
 * and returning a memoized version of the status.
 * @example
 * const  {ref} = useTrackImpression(track, {label: 'your-module-name-impression'})
 *
 * if(loading){
 * 		return <Loading />
 * }
 *
 * return <div ref={ref}>...niceCustomCode</div>
 *
 * @example
 * //can also be used as the regular useOnScreen
 *
 * const  {onScreen, ref} = useTrackImpression();
 *
 * React.useEffect(()=> {
 * 		if(onScreen) {
 * 				track.impression({
 * 					label: 'your-module-name-impression'
 * 				});
 * 		}
 * },[onScreen])
 *
 * return <div ref={ref}>...evenNicerCustomCode</div>
 * */
export default function useTrackImpression(moduleTrackingFunction, options) {
    const [status, setStatus] = useState(UseTrackImpressionTrackStatus.initial);
    const elementRef = useRef();
    const onRef = useCallback((node) => {
        if (node != null) {
            elementRef.current = node;
            setStatus((status) => {
                if (status == UseTrackImpressionTrackStatus.initial) {
                    return UseTrackImpressionTrackStatus.rendered;
                }
                return status;
            });
        }
    }, []);
    // wait for element appear on dom
    useEffect(() => {
        if (status != UseTrackImpressionTrackStatus.rendered) {
            return;
        }
        let subscribed = true;
        // check if scope where this function was invoked still valid
        if (!subscribed) {
            return;
        }
        const observer = new IntersectionObserver(([entry], observer) => {
            // Update our state when observer callback fires
            if (status == UseTrackImpressionTrackStatus.rendered && entry.isIntersecting) {
                setStatus(UseTrackImpressionTrackStatus.impressionFired);
                // check in case user did not pass a moduleTrackingFunction
                // and decides to use this hook along with an useEffect
                if (moduleTrackingFunction != null) {
                    moduleTrackingFunction.impression(options);
                }
                observer.disconnect();
            }
        }, {
            rootMargin: '0.5px',
        });
        observer.observe(elementRef.current);
        return () => {
            subscribed = false;
            if (observer != null) {
                observer.disconnect();
            }
        };
    }, [status]);
    return { ref: onRef, onScreen: status == UseTrackImpressionTrackStatus.impressionFired };
}
